import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  Admin, 
  Category, 
  Post, 
  Role,
  Permission,
  LoginRequest, 
  LoginResponse, 
  CreateAdminRequest, 
  UpdateAdminRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreatePostRequest,
  UpdatePostRequest,
  CreateRoleRequest,
  UpdateRoleRequest,
  CreatePermissionRequest,
  UpdatePermissionRequest,
  ApiResponse,
  PostFilters,
  SliderCategory,
  Slider,
} from '../types';

async function getDeviceInfo(): Promise<string> {
  const UAParserImport = await import('ua-parser-js');
  const UAParser = UAParserImport.UAParser || UAParserImport.default || UAParserImport;
  const parser = new UAParser();
  const result = parser.getResult();
  const os = result.os.name ? `${result.os.name} ${result.os.version || ''}`.trim() : '';
  const deviceType = result.device.type || 'Desktop';
  return `${deviceType}, ${os}`;
}

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: (import.meta as any).env?.VITE_API_URL || 'https://localhost:8080/api/v1',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Проверяем, что это 401 ошибка и запрос еще не повторялся
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Проверяем, что это не запрос на обновление токена или авторизацию
          if (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/login')) {
            // Если это запрос на обновление токена и он не удался, очищаем токены и перенаправляем на логин
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            window.location.href = '/login';
            return Promise.reject(error);
          }

          if (this.isRefreshing) {
            // Если уже идет обновление токена, добавляем запрос в очередь
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(() => {
              const token = localStorage.getItem('access_token');
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return this.client(originalRequest);
              } else {
                throw new Error('No token available');
              }
            }).catch((err) => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
              throw new Error('No refresh token available');
            }

            const response = await this.refreshToken(refreshToken);
            localStorage.setItem('access_token', response.data.access_token);
            localStorage.setItem('refresh_token', response.data.refresh_token);
            
            // Обрабатываем очередь запросов
            this.failedQueue.forEach(({ resolve }) => {
              resolve();
            });
            this.failedQueue = [];
            
            originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            // Обрабатываем ошибки в очереди
            this.failedQueue.forEach(({ reject }) => {
              reject(refreshError);
            });
            this.failedQueue = [];
            
            // Очищаем токены и перенаправляем на логин
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            
            // Предотвращаем множественные перенаправления
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const device = await getDeviceInfo();
    const response: AxiosResponse<LoginResponse> = await this.client.post('/auth/login', {
      ...credentials,
      device,
    });
    return response.data;
  }

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    const device = await getDeviceInfo();
    const response: AxiosResponse<LoginResponse> = await this.client.post('/auth/refresh', {
      refresh_token: refreshToken,
    }, {
      headers: {
        'X-Device': device,
      },
    });
    return response.data;
  }

  async getMe(): Promise<ApiResponse<Admin>> {
    const response: AxiosResponse<ApiResponse<Admin>> = await this.client.get('/auth/me');
    return response.data;
  }

  async getSessions(): Promise<any> {
    return this.client.get('/auth/sessions');
  }
  async terminateSession(id: string): Promise<void> {
    await this.client.delete(`/auth/sessions/${id}`);
  }
  async logoutAllSessions(): Promise<void> {
    await this.client.post('/auth/logout_all');
  }
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await this.client.patch('/auth/change_password', {
      old_password: oldPassword,
      new_password: newPassword,
    });
  }

  // Admin endpoints
  async getAdmins(params?: {
    page?: number;
    limit?: number;
    search?: string;
    active?: boolean;
  }): Promise<ApiResponse<Admin[]>> {
    const response: AxiosResponse<ApiResponse<Admin[]>> = await this.client.get('/admins', { params });
    return response.data;
  }

  async getAdmin(id: string): Promise<ApiResponse<Admin>> {
    const response: AxiosResponse<ApiResponse<Admin>> = await this.client.get(`/admins/${id}`);
    return response.data;
  }

  async createAdmin(data: CreateAdminRequest): Promise<ApiResponse<Admin>> {
    const response: AxiosResponse<ApiResponse<Admin>> = await this.client.post('/admins', data);
    return response.data;
  }

  async updateAdmin(id: string, data: UpdateAdminRequest): Promise<ApiResponse<Admin>> {
    const response: AxiosResponse<ApiResponse<Admin>> = await this.client.put(`/admins/${id}`, data);
    return response.data;
  }

  async deleteAdmin(id: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.client.delete(`/admins/${id}`);
    return response.data;
  }

  // Category endpoints
  async getCategories(params?: {
    page?: number;
    limit?: number;
    search?: string;
    active?: boolean;
    is_default?: boolean;
  }): Promise<ApiResponse<Category[]>> {
    const response: AxiosResponse<ApiResponse<Category[]>> = await this.client.get('/categories', { params });
    return response.data;
  }

  async getCategory(id: string): Promise<ApiResponse<Category>> {
    const response: AxiosResponse<ApiResponse<Category>> = await this.client.get(`/categories/${id}`);
    return response.data;
  }

  async createCategory(data: CreateCategoryRequest): Promise<ApiResponse<Category>> {
    const response: AxiosResponse<ApiResponse<Category>> = await this.client.post('/categories', data);
    return response.data;
  }

  async updateCategory(id: string, data: UpdateCategoryRequest): Promise<ApiResponse<Category>> {
    const response: AxiosResponse<ApiResponse<Category>> = await this.client.put(`/categories/${id}`, data);
    return response.data;
  }

  async deleteCategory(id: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.client.delete(`/categories/${id}`);
    return response.data;
  }

  async setDefaultCategory(id: string): Promise<ApiResponse<Category>> {
    const response: AxiosResponse<ApiResponse<Category>> = await this.client.put(`/categories/${id}/default`);
    return response.data;
  }

  // Post endpoints
  async getPosts(params?: PostFilters): Promise<ApiResponse<Post[]>> {
    const response: AxiosResponse<ApiResponse<Post[]>> = await this.client.get('/posts', { params });
    return response.data;
  }

  async getPost(id: string): Promise<ApiResponse<Post>> {
    const response: AxiosResponse<ApiResponse<Post>> = await this.client.get(`/posts/${id}`);
    return response.data;
  }

  async createPost(data: CreatePostRequest): Promise<ApiResponse<Post>> {
    const response: AxiosResponse<ApiResponse<Post>> = await this.client.post('/posts', data);
    return response.data;
  }

  async updatePost(id: string, data: UpdatePostRequest): Promise<ApiResponse<Post>> {
    const response: AxiosResponse<ApiResponse<Post>> = await this.client.put(`/posts/${id}`, data);
    return response.data;
  }

  async deletePost(id: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.client.delete(`/posts/${id}`);
    return response.data;
  }

  // Role endpoints
  async getRoles(params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<Role[]>> {
    const response: AxiosResponse<ApiResponse<Role[]>> = await this.client.get('/roles', { params });
    return response.data;
  }

  async getRole(id: string): Promise<ApiResponse<Role>> {
    const response: AxiosResponse<ApiResponse<Role>> = await this.client.get(`/roles/${id}`);
    return response.data;
  }

  async createRole(data: CreateRoleRequest): Promise<ApiResponse<Role>> {
    const response: AxiosResponse<ApiResponse<Role>> = await this.client.post('/roles', data);
    return response.data;
  }

  async updateRole(id: string, data: UpdateRoleRequest): Promise<ApiResponse<Role>> {
    const response: AxiosResponse<ApiResponse<Role>> = await this.client.put(`/roles/${id}`, data);
    return response.data;
  }

  async deleteRole(id: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.client.delete(`/roles/${id}`);
    return response.data;
  }

  // Permission endpoints
  async getPermissions(params?: { page?: number; limit?: number; search?: string; resource?: string; action?: string }): Promise<ApiResponse<Permission[]>> {
    const response: AxiosResponse<ApiResponse<Permission[]>> = await this.client.get('/permissions', { params });
    return response.data;
  }

  async getPermission(id: string): Promise<ApiResponse<Permission>> {
    const response: AxiosResponse<ApiResponse<Permission>> = await this.client.get(`/permissions/${id}`);
    return response.data;
  }

  async createPermission(data: CreatePermissionRequest): Promise<ApiResponse<Permission>> {
    const response: AxiosResponse<ApiResponse<Permission>> = await this.client.post('/permissions', data);
    return response.data;
  }

  async updatePermission(id: string, data: UpdatePermissionRequest): Promise<ApiResponse<Permission>> {
    const response: AxiosResponse<ApiResponse<Permission>> = await this.client.put(`/permissions/${id}`, data);
    return response.data;
  }

  async deletePermission(id: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.client.delete(`/permissions/${id}`);
    return response.data;
  }

  // Online status endpoints
  async updateOnlineStatus(): Promise<void> {
    await this.client.post('/online/update');
  }

  async getOnlineAdmins(): Promise<ApiResponse<Admin[]>> {
    const response: AxiosResponse<ApiResponse<Admin[]>> = await this.client.get('/online/admins');
    return response.data;
  }

  async setOfflineStatus(): Promise<void> {
    await this.client.post('/online/offline');
  }

  // Image upload endpoint
  async uploadImage(formData: FormData, folder?: string): Promise<ApiResponse<{ url: string }>> {
    if (folder) {
      formData.append('folder', folder);
    }
    const response: AxiosResponse<ApiResponse<{ url: string }>> = await this.client.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Video upload endpoint
  async uploadVideo(formData: FormData): Promise<ApiResponse<{ url: string }>> {
    const response: AxiosResponse<ApiResponse<{ url: string }>> = await this.client.post('/upload/video', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Document upload endpoint (pdf, doc, xls, etc.)
  async uploadDocument(file: File): Promise<ApiResponse<{ url: string; label: string }>> {
    const formData = new FormData();
    formData.append('document', file);
    const response: AxiosResponse<ApiResponse<{ url: string; label: string }>> = await this.client.post(
      '/upload/document',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  // --- SLIDER CATEGORIES ---
  async getSliderCategories(): Promise<ApiResponse<SliderCategory[]>> {
    const response = await this.client.get('/slider-categories');
    return response.data;
  }
  async getSliderCategory(id: string): Promise<ApiResponse<SliderCategory>> {
    const response = await this.client.get(`/slider-categories/${id}`);
    return response.data;
  }
  async createSliderCategory(data: Partial<SliderCategory>): Promise<ApiResponse<SliderCategory>> {
    const response = await this.client.post('/slider-categories', data);
    return response.data;
  }
  async updateSliderCategory(id: string, data: Partial<SliderCategory>): Promise<ApiResponse<SliderCategory>> {
    const response = await this.client.put(`/slider-categories/${id}`, data);
    return response.data;
  }
  async deleteSliderCategory(id: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.delete(`/slider-categories/${id}`);
    return response.data;
  }

  // --- SLIDERS ---
  async getSliders(params?: { category_id?: string }): Promise<ApiResponse<Slider[]>> {
    const response = await this.client.get('/sliders', { params });
    return response.data;
  }
  async getSlider(id: string): Promise<ApiResponse<Slider>> {
    const response = await this.client.get(`/sliders/${id}`);
    return response.data;
  }
  async createSlider(data: Partial<Slider>): Promise<ApiResponse<Slider>> {
    const response = await this.client.post('/sliders', data);
    return response.data;
  }
  async updateSlider(id: string, data: Partial<Slider>): Promise<ApiResponse<Slider>> {
    const response = await this.client.put(`/sliders/${id}`, data);
    return response.data;
  }
  async deleteSlider(id: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.delete(`/sliders/${id}`);
    return response.data;
  }

  // Helper function to get full media URL (image or video)
  getMediaUrl(relativePath: string): string {
    if (!relativePath) return '';
    // Если уже абсолютный URL
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath;
    }
    // Преобразуем относительные пути в абсолютные с базовым URL API
    const baseUrl = 'https://api.parkdubki.ru';
    // Убеждаемся, что путь начинается с /
    const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
    return `${baseUrl}${path}`;
  }

  // Deprecated: use getMediaUrl
  getImageUrl(relativePath: string): string {
    return this.getMediaUrl(relativePath);
  }
}

export const apiClient = new ApiClient(); 